/**
 * /app/paid-media — agency paid-media campaigns list (Task 041 §5,
 * optional).
 *
 * Wired against `list-ad-accounts` (passing no `client_id`),
 * `list-paid-media-campaigns` (no `client_id`), `list-paid-media-ad-sets`,
 * `list-paid-media-ads`. Both `list-ad-accounts` and
 * `list-paid-media-campaigns` safely accept omitted `client_id` (Task
 * 030 / 032) — RLS handles per-row scoping.
 *
 * The `paid_campaigns.platform` field is flattened from
 * `ad_accounts.platform` via PostgREST embed. When the actor has
 * `paid_media.campaign.read` but not `paid_media.account.read`, the
 * embed resolves to `null` (Task 032 §7.1) — the UI surfaces "—".
 *
 * Each campaign row surfaces the owning client so the agency view
 * stays useful at a glance.
 *
 * Mutation surfaces (record-media-buyer-action, budget edit) are
 * deferred — action buttons render `.btn-coming-soon`.
 */

import { useEffect, useMemo, useState } from 'react';
import { isApiError } from '../api/client';
import { useClientScope } from '../context/ClientScopeProvider';
import {
  listAdAccounts,
  listPaidMediaAdSets,
  listPaidMediaAds,
  listPaidMediaCampaigns,
} from '../api/endpoints';
import type {
  AdAccountRow,
  AdRow,
  AdSetRow,
  PaidCampaignRow,
  PaidMediaEntityStatus,
  PaidMediaPlatform,
} from '../api/types';
import { CampaignMetricsPanel } from '../components/paid-media/CampaignMetricsPanel';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../components/states';

type ListState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly accounts: ReadonlyArray<AdAccountRow>;
      readonly campaigns: ReadonlyArray<PaidCampaignRow>;
    }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly campaign: PaidCampaignRow;
      readonly adSets: ReadonlyArray<AdSetRow>;
      readonly selectedAdSetId: string | null;
      readonly ads: ReadonlyArray<AdRow>;
      readonly adsLoading: boolean;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

const PLATFORM_TABS: ReadonlyArray<{
  readonly value: PaidMediaPlatform | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل المنصات' },
  { value: 'meta_ads', label: 'Meta' },
  { value: 'google_ads', label: 'Google' },
  { value: 'tiktok_ads', label: 'TikTok' },
  { value: 'snapchat_ads', label: 'Snapchat' },
];

const STATUS_FILTERS: ReadonlyArray<{
  readonly value: PaidMediaEntityStatus | 'all';
  readonly label: string;
}> = [
  { value: 'all', label: 'كل الحالات' },
  { value: 'active', label: 'نشطة' },
  { value: 'paused', label: 'متوقفة' },
  { value: 'completed', label: 'منتهية' },
  { value: 'archived', label: 'مؤرشفة' },
];

export function AgencyPaidMediaPage() {
  const { clients, status: clientScopeStatus } = useClientScope();
  const [platform, setPlatform] = useState<PaidMediaPlatform | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<PaidMediaEntityStatus | 'all'>('all');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) map.set(c.id, c.name);
    return map;
  }, [clients]);

  useEffect(() => {
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    // Task 106 (T103-C): these list EFs are now keyset-paginated
    // (default 100 rows/page). This page consumes the FIRST PAGE ONLY;
    // the cursor-walk / "load more" UI is deferred to the T103-C-fe
    // follow-up. The api client already accepts `limit` / `cursor`.
    Promise.all([
      listAdAccounts({
        platform: platform === 'all' ? undefined : platform,
        signal: ctrl.signal,
      }),
      listPaidMediaCampaigns({
        platform: platform === 'all' ? undefined : platform,
        status: statusFilter === 'all' ? undefined : statusFilter,
        signal: ctrl.signal,
      }),
    ])
      .then(([accountsData, campaignsData]) => {
        if (ctrl.signal.aborted) return;
        setList({
          status: 'ready',
          accounts: accountsData.accounts,
          campaigns: campaignsData.campaigns,
        });
        if (campaignsData.campaigns.length > 0 && campaignsData.campaigns[0]) {
          setSelectedCampaignId((prev) =>
            prev && campaignsData.campaigns.some((c) => c.id === prev)
              ? prev
              : campaignsData.campaigns[0]!.id,
          );
        } else {
          setSelectedCampaignId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [platform, statusFilter, reloadKey]);

  useEffect(() => {
    if (!selectedCampaignId || list.status !== 'ready') {
      setDetail({ status: 'idle' });
      return;
    }
    const campaign = list.campaigns.find((c) => c.id === selectedCampaignId);
    if (!campaign) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    // Task 106 (T103-C): list-paid-media-ad-sets / -ads are keyset-
    // paginated; the detail panel shows the first page only (T103-C-fe).
    listPaidMediaAdSets(campaign.id, { signal: ctrl.signal })
      .then((adSetsData) => {
        if (ctrl.signal.aborted) return;
        const firstAdSet = adSetsData.ad_sets[0];
        setDetail({
          status: 'ready',
          campaign,
          adSets: adSetsData.ad_sets,
          selectedAdSetId: firstAdSet ? firstAdSet.id : null,
          ads: [],
          adsLoading: !!firstAdSet,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedCampaignId, list]);

  // Effect dep is the primitive `activeAdSetId` (not the whole `detail` object)
  // so setDetail calls inside the effect don't re-fire it. Mirrors the
  // Task 038 client-tab fix (Codex round-1 P2 on PaidMediaPage).
  const activeAdSetId = detail.status === 'ready' ? detail.selectedAdSetId : null;
  useEffect(() => {
    if (!activeAdSetId) return;
    const adSetId = activeAdSetId;
    const ctrl = new AbortController();
    setDetail((prev) =>
      prev.status === 'ready' && prev.selectedAdSetId === adSetId && !prev.adsLoading
        ? { ...prev, adsLoading: true }
        : prev,
    );
    listPaidMediaAds(adSetId, { signal: ctrl.signal })
      .then((adsData) => {
        if (ctrl.signal.aborted) return;
        setDetail((prev) =>
          prev.status === 'ready' && prev.selectedAdSetId === adSetId
            ? { ...prev, ads: adsData.ads, adsLoading: false }
            : prev,
        );
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setDetail((prev) =>
          prev.status === 'ready' && prev.selectedAdSetId === adSetId
            ? { ...prev, ads: [], adsLoading: false }
            : prev,
        );
        console.error('list-paid-media-ads failed', err);
      });
    return () => ctrl.abort();
  }, [activeAdSetId]);

  const accountsByPlatform = useMemo(() => {
    if (list.status !== 'ready') return new Map<PaidMediaPlatform, AdAccountRow[]>();
    const map = new Map<PaidMediaPlatform, AdAccountRow[]>();
    for (const account of list.accounts) {
      const bucket = map.get(account.platform);
      if (bucket) bucket.push(account);
      else map.set(account.platform, [account]);
    }
    return map;
  }, [list]);

  const formatClient = (clientId: string | null): string => {
    if (!clientId) return 'على مستوى الوكالة';
    const cached = clientNameById.get(clientId);
    if (cached) return cached;
    if (clientScopeStatus === 'loading' || clientScopeStatus === 'idle') return '—';
    return `عميل غير متاح · ${clientId.slice(0, 8)}`;
  };

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          الإعلانات <em className="serif italic">المدفوعة</em>
        </h1>
        <p className="body" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          الحملات والحسابات الإعلانية على مستوى الوكالة. اختر منصة وحالة لتصفية النتائج.
        </p>
      </div>

      <div className="tabs" role="tablist">
        {PLATFORM_TABS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="tab"
            className={p.value === platform ? 'tab active' : 'tab'}
            onClick={() => setPlatform(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="toolbar-row">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={statusFilter === f.value ? 'chip on' : 'chip'}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.status === 'loading' ? <LoadingState title="جاري تحميل الإعلانات…" /> : null}
      {list.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الإعلانات"
          error={list.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {list.status === 'ready' ? (
        <>
          <AdAccountsBlock
            platform={platform}
            accounts={list.accounts}
            accountsByPlatform={accountsByPlatform}
            formatClient={formatClient}
          />
          {list.campaigns.length === 0 ? (
            <EmptyState title="لا توجد حملات مطابقة" description="جرّب فلتر آخر أو منصة أخرى." />
          ) : (
            <div className="workspace-split">
              <aside className="workspace-list">
                <div className="sec-title">
                  <h2>الحملات</h2>
                  <span className="meta">{list.campaigns.length}</span>
                </div>
                <ul className="row-list">
                  {list.campaigns.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={
                          c.id === selectedCampaignId ? 'row-card row-card-active' : 'row-card'
                        }
                        onClick={() => setSelectedCampaignId(c.id)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <strong style={{ fontSize: 13 }}>{c.name}</strong>
                          <span className={`pill pill-${paidStatusVariant(c.status)}`}>
                            {paidStatusLabel(c.status)}
                          </span>
                        </div>
                        <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                          {platformLabel(c.platform)} · {formatClient(c.client_id)}
                        </div>
                        <div className="tiny" style={{ marginTop: 2, color: 'var(--muted)' }}>
                          {c.objective ?? 'بدون هدف'}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>
              <section className="workspace-detail">
                <CampaignDetail
                  state={detail}
                  clientLabel={formatClient(
                    detail.status === 'ready' ? detail.campaign.client_id : null,
                  )}
                  onSelectAdSet={(adSetId) => {
                    setDetail((prev) =>
                      prev.status === 'ready' && prev.selectedAdSetId !== adSetId
                        ? {
                            ...prev,
                            selectedAdSetId: adSetId,
                            ads: [],
                            adsLoading: true,
                          }
                        : prev,
                    );
                  }}
                />
              </section>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function AdAccountsBlock({
  platform,
  accounts,
  accountsByPlatform,
  formatClient,
}: {
  readonly platform: PaidMediaPlatform | 'all';
  readonly accounts: ReadonlyArray<AdAccountRow>;
  readonly accountsByPlatform: Map<PaidMediaPlatform, AdAccountRow[]>;
  readonly formatClient: (clientId: string | null) => string;
}) {
  if (accounts.length === 0) {
    return (
      <div className="card" style={{ padding: 18 }}>
        <div className="sec-title">
          <h2>حسابات الإعلانات</h2>
          <span className="meta">ad_accounts</span>
        </div>
        <EmptyState
          title="لا توجد حسابات مرتبطة"
          description="اربط حساب إعلانات Meta أو Google لتبدأ بتتبع الحملات."
        />
      </div>
    );
  }

  if (platform === 'all') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
          gap: 14,
        }}
      >
        {Array.from(accountsByPlatform.entries()).map(([p, list]) => (
          <div key={p} className="card" style={{ padding: 18 }}>
            <div className="sec-title">
              <h2>{platformLabel(p)}</h2>
              <span className="meta">{list.length} حساب</span>
            </div>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {list.map((a) => (
                <li key={a.id} className="tiny">
                  <strong style={{ color: 'var(--ink)', fontSize: 12 }}>{a.account_name}</strong>
                  <span style={{ color: 'var(--muted)', marginInlineStart: 6 }}>
                    {formatClient(a.client_id)}
                  </span>
                  <span
                    className={`pill pill-${adAccountStatusVariant(a.status)}`}
                    style={{ marginInlineStart: 8, fontSize: 10 }}
                  >
                    {adAccountStatusLabel(a.status)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="sec-title">
        <h2>{platformLabel(platform)}</h2>
        <span className="meta">{accounts.length} حساب</span>
      </div>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {accounts.map((a) => (
          <li
            key={a.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-sm)',
            }}
          >
            <div>
              <strong style={{ fontSize: 13 }}>{a.account_name}</strong>
              <div className="tiny" style={{ color: 'var(--muted)' }}>
                {formatClient(a.client_id)}
                {a.currency_code ? ` · ${a.currency_code}` : ''}
              </div>
            </div>
            <span className={`pill pill-${adAccountStatusVariant(a.status)}`}>
              {adAccountStatusLabel(a.status)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CampaignDetail({
  state,
  clientLabel,
  onSelectAdSet,
}: {
  readonly state: DetailState;
  readonly clientLabel: string;
  readonly onSelectAdSet: (adSetId: string) => void;
}) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر حملة لعرض التفاصيل"
        description="اضغط أي حملة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل تفاصيل الحملة…" />;
  if (state.status === 'not-found')
    return (
      <NotFoundState
        title="الحملة غير متاحة"
        description="ربما تم أرشفتها أو لا تملك صلاحية فتحها."
      />
    );
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل الحملة" error={state.error} />;

  const { campaign, adSets, selectedAdSetId, ads, adsLoading } = state;
  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{campaign.name}</h2>
          <span className="meta">
            {platformLabel(campaign.platform)} · {paidStatusLabel(campaign.status)}
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="العميل" value={clientLabel} />
          <Field label="الهدف" value={campaign.objective} />
          <Field label="نوع الشراء" value={campaign.buying_type} />
          <Field
            label="بدأت"
            value={
              campaign.started_at ? new Date(campaign.started_at).toLocaleDateString('ar-SA') : null
            }
          />
          <Field
            label="انتهت"
            value={
              campaign.ended_at ? new Date(campaign.ended_at).toLocaleDateString('ar-SA') : null
            }
          />
          <Field
            label="آخر مزامنة"
            value={
              campaign.last_synced_at
                ? new Date(campaign.last_synced_at).toLocaleString('ar-SA')
                : null
            }
          />
          <Field label="معرف المنصة" value={campaign.platform_campaign_id} mono />
        </div>
      </div>

      <CampaignMetricsPanel paidCampaignId={campaign.id} adSets={adSets} />

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>المجموعات الإعلانية (Ad Sets)</h2>
          <span className="meta">{adSets.length}</span>
        </div>
        {adSets.length === 0 ? (
          <EmptyState
            title="لا توجد مجموعات إعلانية"
            description="ستظهر هنا بمجرد مزامنة الحملة من المنصة."
          />
        ) : (
          <ul className="row-list" style={{ marginBottom: 14 }}>
            {adSets.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={s.id === selectedAdSetId ? 'row-card row-card-active' : 'row-card'}
                  onClick={() => onSelectAdSet(s.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <strong style={{ fontSize: 13 }}>{s.name}</strong>
                    <span className={`pill pill-${paidStatusVariant(s.status)}`}>
                      {paidStatusLabel(s.status)}
                    </span>
                  </div>
                  <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                    {s.optimization_goal ?? 'بدون هدف تحسين'} ·{' '}
                    {s.billing_event ?? 'بدون billing event'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="sec-title" style={{ marginTop: 8 }}>
          <h2>الإعلانات</h2>
          <span className="meta">{adsLoading ? 'جاري التحميل…' : `${ads.length}`}</span>
        </div>
        {adsLoading ? (
          <LoadingState title="جاري تحميل الإعلانات…" variant="inline" />
        ) : ads.length === 0 ? (
          <EmptyState
            title="لا توجد إعلانات في هذه المجموعة"
            description="ستظهر هنا بمجرد مزامنة المجموعة."
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>اسم الإعلان</th>
                <th>الحالة</th>
                <th>معرف المنصة</th>
                <th>آخر مزامنة</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="name">{a.name}</span>
                    {a.creative_external_id ? (
                      <div className="sub">{a.creative_external_id}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`pill pill-${paidStatusVariant(a.status)}`}>
                      {paidStatusLabel(a.status)}
                    </span>
                  </td>
                  <td className="num">{a.platform_ad_id}</td>
                  <td>
                    {a.last_synced_at ? new Date(a.last_synced_at).toLocaleString('ar-SA') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn-coming-soon" disabled>
          إيقاف الحملة (قريباً)
        </button>
        <button type="button" className="btn-coming-soon" disabled>
          تعديل الميزانية (قريباً)
        </button>
      </div>
    </div>
  );
}

function platformLabel(platform: PaidMediaPlatform | null): string {
  if (!platform) return '—';
  switch (platform) {
    case 'meta_ads':
      return 'Meta';
    case 'google_ads':
      return 'Google';
    case 'tiktok_ads':
      return 'TikTok';
    case 'snapchat_ads':
      return 'Snapchat';
    default:
      return platform;
  }
}

function paidStatusLabel(status: PaidMediaEntityStatus): string {
  switch (status) {
    case 'active':
      return 'نشطة';
    case 'paused':
      return 'متوقفة';
    case 'completed':
      return 'منتهية';
    case 'archived':
      return 'مؤرشفة';
    case 'unknown':
      return 'غير معروفة';
    default:
      return status;
  }
}

function paidStatusVariant(status: PaidMediaEntityStatus): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'paused':
      return 'paused';
    case 'completed':
      return 'info';
    case 'archived':
      return 'archived';
    case 'unknown':
      return 'draft';
    default:
      return 'draft';
  }
}

function adAccountStatusLabel(status: AdAccountRow['status']): string {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'reauthorization_required':
      return 'يحتاج إعادة ربط';
    case 'disabled':
      return 'معطل';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

function adAccountStatusVariant(status: AdAccountRow['status']): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'reauthorization_required':
      return 'review';
    case 'disabled':
      return 'error';
    case 'archived':
      return 'archived';
    default:
      return 'draft';
  }
}

function Field({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
  readonly mono?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body" style={mono ? { fontFamily: 'var(--font-mono)', fontSize: 11 } : {}}>
        {value && value.length > 0 ? value : '—'}
      </span>
    </div>
  );
}
